/**
 * ChordInlineBindingService
 * 
 * Transforms traditional chord-above-text format into inline segments where chords
 * are bound to specific character positions in lyrics. This enables proper text wrapping
 * where chords move with their associated words/syllables.
 * 
 * Key Features:
 * - Binds chords to exact character positions in lyrics
 * - Preserves chord decorations (parentheses, slashes, repetition markers)
 * - Handles syllable markers and special characters
 * - Maintains whitespace fidelity for proper alignment
 */

interface Chord {
  chord: string;
  index: number;
  prefix: string;
  suffix: string;
}

interface Line {
  type: 'chord' | 'text';
  content: Chord[] | string;
  lineNumber?: number;
}

interface Song {
  lines: Line[];
  tags?: string[];
  keys?: string[];
  artists?: string[];
  title?: string;
  displayKeys?: string[];
  lineStart?: number;
  lineEnd?: number;
  notation?: string;
  sections?: Section[];
  currentPosition?: number;
  originalPosition?: number;
  originalKey?: string;
}

interface Section {
  typeOfVerse: string;
  key: string | null;
  number: number;
  content: Line[][];
}

/**
 * Represents a segment of text with an optional chord annotation
 */
export interface InlineChordSegment {
  text: string;           // The text content (word, syllable, or whitespace)
  chord?: string;         // Optional chord with decorations (e.g., "(d)", "F/C", "A7")
  chordPrefix?: string;   // Whitespace before chord for alignment
  chordSuffix?: string;   // Trailing characters after chord (e.g., "/x3", ")")
  isWhitespace: boolean;  // True if this segment is only whitespace
  textPosition: number;   // Original character position in text line
}

/**
 * Line with inline chord segments
 */
export interface InlineLine {
  type: 'inline-lyrics' | 'text' | 'chord' | 'meta';
  segments?: InlineChordSegment[];  // For inline-lyrics
  content?: Chord[] | string;        // For text/chord/meta (unchanged)
  lineNumber?: number;
}

/**
 * Song with inline chord bindings
 */
export interface InlineSong extends Omit<Song, 'lines' | 'sections'> {
  lines: InlineLine[];
  sections?: InlineSection[];
}

export interface InlineSection extends Omit<Section, 'content'> {
  content: InlineLine[][];
}

/**
 * Extracts chord with all its decorations (parentheses, suffixes, etc.)
 */
function extractFullChord(chord: Chord): {
  fullChord: string;
  prefixDecorations: string;
  suffixDecorations: string;
  cleanPrefix: string;
} {
  let fullChord = chord.chord;
  let prefixDecorations = '';
  let suffixDecorations = '';

  // Check for leading decorations in prefix (e.g., "(", "[", "{", "/")
  // We allow spaces after the decoration for cases like "( D"
  const prefixMatch = chord.prefix.match(/([(\[{/]+\s*)$/);
  if (prefixMatch && prefixMatch[1]) {
    prefixDecorations = prefixMatch[1];
    fullChord = prefixDecorations + fullChord;
  }

  // Check for trailing decorations in suffix (e.g., ")", "]", "}", "/x3", "/2", "/Fis")
  // Also capture slash chords like "/d", "/Fis", "/A#"
  // Pattern: closing brackets, repetition markers, or slash followed by chord letter
  const suffixMatch = chord.suffix.match(/^(\s*[)\]}]|\s*\/x\d+|\s*\/\d+|\s*\/[A-Ha-h](#|is|b)?)/);
  if (suffixMatch && suffixMatch[1]) {
    suffixDecorations = suffixMatch[1].trim();
    fullChord = fullChord + suffixDecorations;
  }

  // Clean prefix is the prefix without the decorations
  const cleanPrefix = prefixDecorations ? chord.prefix.slice(0, -prefixDecorations.length) : chord.prefix;

  return { fullChord, prefixDecorations, suffixDecorations, cleanPrefix };
}

/**
 * Merges consecutive chords that form a slash chord (e.g., F and /d -> F/d)
 */
function mergeSlashChords(chordLine: Chord[]): Chord[] {
  const merged: Chord[] = [];

  for (let i = 0; i < chordLine.length; i++) {
    const current = chordLine[i];
    const next = chordLine[i + 1];

    // Check for slash chord pattern:
    // 1. Next chord starts with / in its prefix (traditional)
    // 2. Or current chord ends with / in its suffix (post-LineProcessor fix)
    const isSlashChord = (next && next.prefix.trim().startsWith('/')) ||
      (current.suffix.trim().endsWith('/'));

    if (next && isSlashChord) {
      // Merge current and next into a slash chord
      // Determine how to combine them (ensure we don't double the slash)
      let combinedChord = current.chord;
      if (!combinedChord.endsWith('/') && !next.chord.startsWith('/')) {
        combinedChord += '/';
      }
      combinedChord += next.chord;

      merged.push({
        chord: combinedChord,
        index: current.index,
        prefix: current.prefix,
        suffix: next.suffix
      });
      i++; // Skip the next chord since we merged it
    } else {
      merged.push(current);
    }
  }

  return merged;
}

/**
 * Splits text into segments based on chord positions.
 * Ensures words stay together for proper wrapping - each segment contains
 * either one complete word OR whitespace, never multiple words.
 */
function createInlineSegments(chordLine: Chord[], textLine: string): InlineChordSegment[] {
  const segments: InlineChordSegment[] = [];

  if (chordLine.length === 0) {
    // No chords - split text into word and whitespace segments for proper wrapping
    splitTextIntoWordSegments(textLine, 0, segments);
    return segments;
  }

  // Merge consecutive chords that form slash chords (e.g., F + /d -> F/d)
  const mergedChords = mergeSlashChords(chordLine);

  let currentPos = 0;

  mergedChords.forEach((chord, chordIndex) => {
    const { fullChord, prefixDecorations, cleanPrefix } = extractFullChord(chord);

    // Calculate the actual position where chord should be bound
    let chordBindPosition = chord.index;

    // Handle text before chord (if any)
    if (currentPos < chordBindPosition) {
      const textBefore = textLine.slice(currentPos, chordBindPosition);
      if (textBefore.length > 0) {
        // Split text before chord into word/whitespace segments
        splitTextIntoWordSegments(textBefore, currentPos, segments);
      }
      currentPos = chordBindPosition;
    }

    // Find the end position for text under this chord
    // Look ahead to next chord or end of line
    let textEndPos: number;
    if (chordIndex < mergedChords.length - 1) {
      textEndPos = mergedChords[chordIndex + 1].index;
    } else {
      textEndPos = textLine.length;
    }

    // Extract text under this chord
    const textUnderChord = textLine.slice(chordBindPosition, textEndPos);

    // Extract just the first word under this chord
    // Regex: captures first word (non-whitespace chars) and any trailing content
    const match = textUnderChord.match(/^([^\s]+)(.*)$/);

    if (match) {
      const [, firstWord, remaining] = match;

      // Create segment with chord bound to ONLY the first word
      segments.push({
        text: firstWord,
        chord: fullChord,
        chordPrefix: cleanPrefix,
        isWhitespace: false,
        textPosition: chordBindPosition
      });

      currentPos = chordBindPosition + firstWord.length;

      // Handle any remaining text after the word (whitespace + possibly more words)
      if (remaining.length > 0) {
        // Split remaining text into word/whitespace segments
        splitTextIntoWordSegments(remaining, currentPos, segments);
        currentPos = chordBindPosition + textUnderChord.length;
      }
    } else if (textUnderChord.trim() === '') {
      // Chord over whitespace only
      segments.push({
        text: textUnderChord,
        chord: fullChord,
        chordPrefix: cleanPrefix,
        isWhitespace: true,
        textPosition: chordBindPosition
      });
      currentPos = textEndPos;
    } else {
      // Fallback: shouldn't reach here, but handle just in case
      segments.push({
        text: textUnderChord,
        chord: fullChord,
        chordPrefix: cleanPrefix,
        isWhitespace: textUnderChord.trim() === '',
        textPosition: chordBindPosition
      });
      currentPos = textEndPos;
    }
  });

  // Add any remaining text after last chord
  if (currentPos < textLine.length) {
    const remainingText = textLine.slice(currentPos);
    splitTextIntoWordSegments(remainingText, currentPos, segments);
  }

  return segments;
}

/**
 * Helper function to split text into word and whitespace segments.
 * Ensures each word is a separate segment for proper wrapping.
 * 
 * @param text - The text to split
 * @param startPosition - The starting position in the original text line
 * @param segments - The array to append segments to
 */
function splitTextIntoWordSegments(
  text: string,
  startPosition: number,
  segments: InlineChordSegment[]
): void {
  if (text.length === 0) return;

  let pos = 0;

  // Regex to match alternating pattern of words and whitespace
  // Captures either word characters (non-whitespace) or whitespace
  const pattern = /([^\s]+)|(\s+)/g;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match[1]) {
      // Found a word (non-whitespace)
      segments.push({
        text: match[1],
        isWhitespace: false,
        textPosition: startPosition + match.index
      });
    } else if (match[2]) {
      // Found whitespace
      segments.push({
        text: match[2],
        isWhitespace: true,
        textPosition: startPosition + match.index
      });
    }
  }
}

/**
 * Converts a chord+text line pair into an inline lyrics line
 */
function convertToInlineLine(chordLine: Line, textLine: Line): InlineLine {
  // Clone chords to avoid modifying original song data
  const originalChords = chordLine.content as Chord[];
  const chords = originalChords.map(c => ({ ...c }));
  let text = textLine.content as string;

  // Calculate the end position of the last chord (position + chord length + suffix)
  if (chords.length > 0) {
    const lastChord = chords[chords.length - 1];
    const { fullChord } = extractFullChord(lastChord);
    const chordLineEndPos = lastChord.index + fullChord.length + lastChord.suffix.length;

    // If chord line extends beyond text line, pad text with spaces
    if (chordLineEndPos > text.length) {
      const paddingNeeded = chordLineEndPos - text.length;
      text = text + ' '.repeat(paddingNeeded);
    }
  }

  const segments = createInlineSegments(chords, text);

  return {
    type: 'inline-lyrics',
    segments,
    lineNumber: textLine.lineNumber
  };
}

/**
 * Service class for converting songs to inline chord format
 */
export class ChordInlineBindingService {
  /**
   * Converts a song with traditional chord-above-text format to inline format
   */
  convertSong(song: Song): InlineSong {
    const inlineLines: InlineLine[] = [];

    let i = 0;
    while (i < song.lines.length) {
      const currentLine = song.lines[i];

      // Check if this is a chord line followed by a text line
      if (
        currentLine.type === 'chord' &&
        i + 1 < song.lines.length &&
        song.lines[i + 1].type === 'text'
        && !/^\s*(?:\/\/|\/)?\s*(?:x\s*\d+|\d+\s*x)\s*$/i.test(song.lines[i + 1].content as string)
      ) {
        // Convert chord+text pair to inline format
        const chordLine = currentLine;
        const textLine = song.lines[i + 1];

        const inlineLine = convertToInlineLine(chordLine, textLine);
        inlineLines.push(inlineLine);

        i += 2; // Skip both lines
      } else {
        // Keep line as-is (standalone text, meta, or standalone chord)
        inlineLines.push({
          type: currentLine.type as any,
          content: currentLine.content,
          lineNumber: currentLine.lineNumber
        });
        i += 1;
      }
    }

    // Convert sections if they exist
    let inlineSections: InlineSection[] | undefined;
    if (song.sections) {
      inlineSections = song.sections.map(section => this.convertSection(section));
    }

    return {
      ...song,
      lines: inlineLines,
      sections: inlineSections
    };
  }

  /**
   * Converts songs array to inline format
   */
  convertSongs(songs: Song[]): InlineSong[] {
    return songs.map(song => this.convertSong(song));
  }

  /**
   * Converts a section's content to inline format
   */
  private convertSection(section: Section): InlineSection {
    const inlineContent: InlineLine[][] = section.content.map(lineGroup => {
      const inlineGroup: InlineLine[] = [];

      let i = 0;
      while (i < lineGroup.length) {
        const currentLine = lineGroup[i];

        // Check if this is a chord line followed by a text line
        if (
          currentLine.type === 'chord' &&
          i + 1 < lineGroup.length &&
          lineGroup[i + 1].type === 'text'
          && !/^\s*(?:\/\/|\/)?\s*(?:x\s*\d+|\d+\s*x)\s*$/i.test(lineGroup[i + 1].content as string)
        ) {
          const chordLine = currentLine;
          const textLine = lineGroup[i + 1];

          const inlineLine = convertToInlineLine(chordLine, textLine);
          inlineGroup.push(inlineLine);

          i += 2;
        } else {
          inlineGroup.push({
            type: currentLine.type as any,
            content: currentLine.content,
            lineNumber: currentLine.lineNumber
          });
          i += 1;
        }
      }

      return inlineGroup;
    });

    return {
      ...section,
      content: inlineContent
    };
  }
}
